'use client';
import React, { useCallback } from 'react';
import {
  CheckCircle, Download, Heart, ArrowRight, Mail, Calendar,
  Hash, Tag, RefreshCw, Shield, Printer,
} from 'lucide-react';
import type { PaymentIntent } from '@stripe/stripe-js';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface DonorData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  giftAid: boolean;
}

interface DonationConfirmationProps {
  paymentIntent: PaymentIntent;
  donorData: DonorData;
  cause: string;
  causeTitle: string;
  frequency: string;
  amount: number;
}

const frequencyLabels: Record<string, string> = {
  daily: 'per day',
  weekly: 'per week',
  monthly: 'per month',
  yearly: 'per year',
};

const frequencyDisplay: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

// ─── Receipt Row ──────────────────────────────────────────────────────────────
function ReceiptRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold font-tabular ${highlight ? 'text-primary text-base' : 'text-foreground'}`}>
        {value}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DonationConfirmation({
  paymentIntent,
  donorData,
  cause,
  causeTitle,
  frequency,
  amount,
}: DonationConfirmationProps) {
  const receiptNumber = paymentIntent.id
    ? `MDF-${paymentIntent.id.slice(-10).toUpperCase()}`
    : `MDF-${Date.now().toString(36).toUpperCase()}`;

  const donorName = `${donorData.firstName} ${donorData.lastName}`;
  const freqLabel = frequencyLabels[frequency] ?? frequency;
  const freqDisplay = frequencyDisplay[frequency] ?? frequency;

  // ── PDF Download ────────────────────────────────────────────────────────────
  const handleDownloadPDF = useCallback(async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      let y = 20;

      // ── Header band ──────────────────────────────────────────────────────────
      doc.setFillColor(37, 99, 235); // primary blue
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('MOURNDIECE Foundation', margin, 17);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Official Donation Receipt', margin, 26);
      doc.text('mourndiecefoundation.org', margin, 33);

      // ── Confirmation badge ────────────────────────────────────────────────────
      y = 55;
      doc.setFillColor(220, 252, 231); // green-100
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F');
      doc.setTextColor(22, 163, 74); // green-600
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('✓  Payment Confirmed — Thank You for Your Generosity!', margin + 6, y + 12);

      // ── Receipt details ───────────────────────────────────────────────────────
      y = 85;
      doc.setTextColor(30, 41, 59); // foreground
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Donation Details', margin, y);

      // Divider
      y += 4;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.line(margin, y, pageWidth - margin, y);

      const rows: [string, string][] = [
        ['Receipt Number', receiptNumber],
        ['Donor Name', donorName],
        ['Email Address', donorData.email],
        ['Cause', causeTitle],
        ['Donation Frequency', freqDisplay],
        ['Amount', `$${amount.toFixed(2)} ${freqLabel}`],
        ['Payment Status', 'Confirmed ✓'],
        ['Transaction ID', paymentIntent.id ?? 'N/A'],
      ];

      y += 8;
      doc.setFontSize(10);

      rows.forEach(([label, value], i) => {
        const rowY = y + i * 12;
        // Alternate row background
        if (i % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, rowY - 5, contentWidth, 12, 'F');
        }
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139); // muted
        doc.text(label, margin + 4, rowY + 2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text(value, pageWidth - margin - 4, rowY + 2, { align: 'right' });
      });

      y += rows.length * 12 + 10;

      // ── Annual impact box ─────────────────────────────────────────────────────
      const annualMultipliers: Record<string, number> = {
        daily: 365, weekly: 52, monthly: 12, yearly: 1,
      };
      const annualTotal = amount * (annualMultipliers[frequency] ?? 1);

      doc.setFillColor(239, 246, 255); // blue-50
      doc.roundedRect(margin, y, contentWidth, 22, 3, 3, 'F');
      doc.setTextColor(37, 99, 235);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Projected Annual Impact', margin + 6, y + 9);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${annualTotal.toLocaleString('en-US')} / year`, margin + 6, y + 18);

      // ── Gift Aid note ─────────────────────────────────────────────────────────
      if (donorData.giftAid) {
        y += 30;
        doc.setFillColor(240, 253, 244);
        doc.roundedRect(margin, y, contentWidth, 14, 3, 3, 'F');
        doc.setTextColor(22, 163, 74);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Gift Aid Declared', margin + 6, y + 6);
        doc.setFont('helvetica', 'normal');
        doc.text('You have confirmed this donation qualifies for UK Gift Aid.', margin + 6, y + 11);
        y += 20;
      } else {
        y += 30;
      }

      // ── Footer ────────────────────────────────────────────────────────────────
      doc.setFillColor(248, 250, 252);
      doc.rect(0, 270, pageWidth, 27, 'F');
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('MOURNDIECE Foundation — Registered Non-Profit', pageWidth / 2, 278, { align: 'center' });
      doc.text('mourndiecefoundation.org  |  This receipt is valid for tax purposes.', pageWidth / 2, 284, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, 290, { align: 'center' });

      doc.save(`MDF-Receipt-${receiptNumber}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
  }, [paymentIntent, donorData, causeTitle, frequency, amount, receiptNumber, freqLabel, freqDisplay, donorName]);

  // ── Annual impact calc ──────────────────────────────────────────────────────
  const annualMultipliers: Record<string, number> = {
    daily: 365, weekly: 52, monthly: 12, yearly: 1,
  };
  const annualTotal = amount * (annualMultipliers[frequency] ?? 1);

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-16">
      <div className="max-w-2xl mx-auto">

        {/* ── Success Header ───────────────────────────────────────────────── */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={42} className="text-green-600" />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-3">
            Thank You for Your Generosity!
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md mx-auto">
            Your donation has been confirmed. A receipt has been sent to{' '}
            <strong className="text-foreground">{donorData.email}</strong>.
          </p>
        </div>

        {/* ── Receipt Card ─────────────────────────────────────────────────── */}
        <div className="bg-card rounded-2xl border border-border shadow-card overflow-hidden mb-6">

          {/* Card Header */}
          <div className="gradient-primary px-6 py-5 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-blue-200 uppercase tracking-widest mb-1">
                Official Donation Receipt
              </div>
              <div className="text-white font-bold text-lg">MOURNDIECE Foundation</div>
            </div>
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
              <Heart size={22} className="text-white" />
            </div>
          </div>

          {/* Receipt Body */}
          <div className="px-6 py-6">

            {/* Receipt Number + Date */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 pb-5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Hash size={14} className="text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Receipt Number</div>
                  <div className="font-bold text-foreground text-sm font-tabular">{receiptNumber}</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Calendar size={14} className="text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Date</div>
                  <div className="font-semibold text-foreground text-sm">
                    {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>

            {/* Donor Info */}
            <div className="flex items-center gap-3 mb-6 p-4 bg-muted/40 rounded-xl">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-sm">
                  {donorData.firstName.charAt(0)}{donorData.lastName.charAt(0)}
                </span>
              </div>
              <div>
                <div className="font-semibold text-foreground text-sm">{donorName}</div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                  <Mail size={11} />
                  {donorData.email}
                </div>
              </div>
            </div>

            {/* Donation Details Table */}
            <div className="mb-6">
              <ReceiptRow label="Cause" value={causeTitle} />
              <ReceiptRow
                label="Donation Frequency"
                value={
                  <span className="flex items-center gap-1.5">
                    <RefreshCw size={12} className="text-primary" />
                    {freqDisplay}
                  </span> as unknown as string
                }
              />
              <ReceiptRow
                label="Amount"
                value={`$${amount.toFixed(2)} ${freqLabel}`}
                highlight
              />
              <ReceiptRow
                label="Payment Status"
                value="Confirmed ✓"
              />
              <ReceiptRow
                label="Transaction ID"
                value={paymentIntent.id ? `...${paymentIntent.id.slice(-12)}` : 'Confirmed'}
              />
            </div>

            {/* Annual Impact */}
            <div className="bg-primary/6 rounded-xl p-4 mb-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground mb-0.5">Projected Annual Impact</div>
                <div className="text-2xl font-bold text-primary font-tabular">
                  ${annualTotal.toLocaleString('en-US')}
                </div>
                <div className="text-xs text-muted-foreground">total per year</div>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Tag size={20} className="text-primary" />
              </div>
            </div>

            {/* Gift Aid Badge */}
            {donorData.giftAid && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-5 flex items-center gap-2.5">
                <CheckCircle size={16} className="text-green-600 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-green-800">Gift Aid Declared</div>
                  <div className="text-xs text-green-700">
                    We can claim an extra 25p for every £1 you donate at no cost to you.
                  </div>
                </div>
              </div>
            )}

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
              {[
                { icon: Shield, text: 'SSL Encrypted' },
                { icon: CheckCircle, text: '100% to your cause' },
                { icon: Heart, text: 'Registered Non-Profit' },
              ].map(({ icon: Icon, text }) => (
                <div key={`trust-${text}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon size={12} className="text-accent" />
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Action Buttons ───────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <button
            onClick={handleDownloadPDF}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-white font-semibold px-6 py-3.5 rounded-xl text-sm hover:bg-primary/90 transition-all shadow-sm"
          >
            <Download size={16} />
            Download PDF Receipt
          </button>
          <button
            onClick={() => window.print()}
            className="flex-1 flex items-center justify-center gap-2 border border-border text-foreground font-semibold px-6 py-3.5 rounded-xl text-sm hover:border-primary/40 transition-all"
          >
            <Printer size={16} />
            Print Receipt
          </button>
        </div>

        {/* ── Navigation ───────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/"
            className="inline-flex items-center justify-center gap-2 btn-primary font-semibold px-8 py-3.5 rounded-xl text-sm"
          >
            Back to Home
            <ArrowRight size={15} />
          </a>
          <a
            href="/donation-screen"
            className="inline-flex items-center justify-center gap-2 font-semibold px-8 py-3.5 rounded-xl text-sm border border-border hover:border-primary/40 text-foreground transition-all"
          >
            Donate Again
          </a>
        </div>

        {/* ── Email Note ───────────────────────────────────────────────────── */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          A copy of this receipt has been sent to <strong>{donorData.email}</strong>. 
          Please check your spam folder if you don't see it within 5 minutes.
        </p>
      </div>
    </div>
  );
}
